import React, { useState, useMemo } from 'react';
import { formatDateTime } from '../../../../utils/formatters';
import { Lead, Task } from '../../../../types';
import { useTasks } from '../../../../contexts/tasks/TasksContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { CheckCircleIcon, TrashIcon, CalendarIcon } from '../../../../components/icons';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { TaskCreateModal } from './TaskCreateModal';
import { useTaskManagement } from '../../../../features/tasks/hooks/useTaskManagement';
import { Button } from '../../../../components/ui/Button';
import { cn } from '@/lib/utils';

interface LeadTasksTabProps {
    lead: Lead;
}

export const LeadTasksTab: React.FC<LeadTasksTabProps> = ({ lead }) => {
    const { tasks: allTasksGlobal } = useTasks();
    const { user } = useAuth();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState<Task | undefined>(undefined);

    const leadTasksRaw = useMemo(
        () => allTasksGlobal.filter((task) => task.leadId === lead.id),
        [allTasksGlobal, lead.id],
    );

    const {
        tasks: filteredTasks,
        statusFilter,
        toggleTaskCompletion,
        deleteTask,
        duplicateTask,
    } = useTaskManagement(leadTasksRaw);

    const groupedTasks = useMemo(() => {
        const overdue: Task[] = [];
        const today: Task[] = [];
        const upcoming: Task[] = [];
        const completed: Task[] = [];

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        filteredTasks.forEach((task) => {
            if (task.isCompleted) {
                completed.push(task);
                return;
            }

            const due = new Date(task.dueDate);
            const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

            if (dueDay < todayStart) overdue.push(task);
            else if (dueDay.getTime() === todayStart.getTime()) today.push(task);
            else upcoming.push(task);
        });

        return { overdue, today, upcoming, completed };
    }, [filteredTasks]);

    const openEditModal = (task: Task) => {
        setTaskToEdit(task);
        setIsCreateModalOpen(true);
    };

    const renderTaskCard = (task: Task) => (
        <article
            key={task.id}
            className="rounded-xl border border-border bg-surface-0 p-s4 transition-all hover:border-border-strong hover:shadow-sm"
        >
            <div className="flex items-start justify-between gap-s3">
                <div className="flex min-w-0 flex-1 items-start gap-s3">
                    <button
                        type="button"
                        onClick={() => toggleTaskCompletion(task)}
                        className={cn(
                            'mt-s1 flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-border',
                            'transition-all hover:border-accent hover:bg-accent-subtle',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                        )}
                        title={task.isCompleted ? 'Reabrir tarefa' : 'Concluir tarefa'}
                        aria-label={task.isCompleted ? 'Reabrir tarefa' : 'Concluir tarefa'}
                        aria-pressed={task.isCompleted}
                    >
                        {task.isCompleted ? (
                            <span className="size-3 rounded-full bg-accent" aria-hidden />
                        ) : null}
                    </button>

                    <div className="min-w-0 flex-1">
                        <h4
                            className={cn(
                                'mb-s1 text-sm font-semibold',
                                task.isCompleted ? 'text-secondary line-through' : 'text-default',
                            )}
                        >
                            {task.title}
                        </h4>
                        {task.description ? (
                            <p className="mb-s2 text-xs text-secondary">{task.description}</p>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-s2 text-xs">
                            <CalendarIcon className="size-4 text-primary" aria-hidden />
                            <span className="text-secondary">{task.type || 'Agendamento'}</span>
                            <span className="text-border-strong" aria-hidden>
                                |
                            </span>
                            <time
                                className="font-mono tabular-nums text-secondary"
                                dateTime={task.dueDate}
                            >
                                {formatDateTime(task.dueDate)}
                            </time>
                        </div>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-s1">
                    <button
                        type="button"
                        onClick={() => toggleTaskCompletion(task)}
                        className="rounded-lg p-s2 text-accent transition-colors hover:bg-accent-subtle"
                        title="Concluir"
                        aria-label="Concluir tarefa"
                    >
                        <CheckCircleIcon className="size-4" aria-hidden />
                    </button>
                    <button
                        type="button"
                        onClick={() => openEditModal(task)}
                        className="rounded-lg p-s2 text-primary transition-colors hover:bg-primary-subtle"
                        title="Editar"
                        aria-label="Editar tarefa"
                    >
                        <AppIcon name="edit" className="size-4" aria-hidden />
                    </button>
                    <button
                        type="button"
                        onClick={() => duplicateTask(task)}
                        className="rounded-lg p-s2 text-secondary transition-colors hover:bg-surface-subtle"
                        title="Duplicar"
                        aria-label="Duplicar tarefa"
                    >
                        <AppIcon name="duplicate" className="size-4" aria-hidden />
                    </button>
                    <button
                        type="button"
                        onClick={() => deleteTask(task.id)}
                        className="rounded-lg p-s2 text-error transition-colors hover:bg-danger-bg"
                        title="Excluir"
                        aria-label="Excluir tarefa"
                    >
                        <TrashIcon className="size-4" aria-hidden />
                    </button>
                </div>
            </div>
        </article>
    );

    const renderSection = (title: string, count: number, tasks: Task[]) => {
        if (tasks.length === 0) return null;

        return (
            <section className="mb-s6" aria-label={title}>
                <h3 className="mb-s3 text-xs font-bold uppercase tracking-wider text-secondary">
                    {title} ({count})
                </h3>
                <div className="space-y-s2">{tasks.map(renderTaskCard)}</div>
            </section>
        );
    };

    return (
        <section aria-label="Tarefas da negociação" className="space-y-s6 py-s4">
            <Button
                type="button"
                onClick={() => {
                    setTaskToEdit(undefined);
                    setIsCreateModalOpen(true);
                }}
                className="min-h-11 w-full touch-manipulation"
            >
                + Nova tarefa
            </Button>

            {filteredTasks.length > 0 ? (
                <>
                    {renderSection('Atrasadas', groupedTasks.overdue.length, groupedTasks.overdue)}
                    {renderSection('Para Hoje', groupedTasks.today.length, groupedTasks.today)}
                    {renderSection('Próximas', groupedTasks.upcoming.length, groupedTasks.upcoming)}

                    {groupedTasks.completed.length > 0 ? (
                        <section
                            className="mt-s8 border-t border-border pt-s4"
                            aria-label="Concluídas"
                        >
                            <h3 className="mb-s3 text-xs font-bold uppercase tracking-wider text-secondary">
                                Concluídas ({groupedTasks.completed.length})
                            </h3>
                            <div className="space-y-s2 opacity-75">
                                {groupedTasks.completed.map(renderTaskCard)}
                            </div>
                        </section>
                    ) : null}
                </>
            ) : (
                <p className="py-s8 text-center text-sm text-muted">
                    {statusFilter.length > 0
                        ? 'Nenhuma tarefa encontrada para este filtro.'
                        : 'Nenhuma tarefa pendente.'}
                </p>
            )}

            <TaskCreateModal
                isOpen={isCreateModalOpen}
                onClose={() => {
                    setIsCreateModalOpen(false);
                    setTaskToEdit(undefined);
                }}
                leadId={lead.id}
                assignedTo={lead.agent || user?.id || undefined}
                // @ts-ignore
                taskToEdit={taskToEdit}
            />
        </section>
    );
};
